'use client';

import { Check } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { filterParamsParser } from '@dolcevia/lib/searchParams';
import { Button } from '@dolcevia/ui/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@dolcevia/ui/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dolcevia/ui/components/ui/popover';
import { useTranslations } from 'next-intl';
import { useQueryState } from 'nuqs';

type TFuncArg = Parameters<
  ReturnType<typeof useTranslations<'Destinations.Hero.Dropdown.Options'>>
>[0];

type Option<UseTranslation extends boolean = true> = {
  value: string;
  label: UseTranslation extends true ? TFuncArg : string;
};

export type DurationDropdownProps<UseTranslation extends boolean = true> = {
  options: Option<UseTranslation>[];
  className?: string;
  placeholder?: string;
  title: string;
  subTitle: string;
  filterKey: string;
  disabled?: boolean;
  useTraduction: UseTranslation;
};

export function DurationDropdown<UseTranslation extends boolean = true>({
  options,
  className,
  placeholder,
  title,
  subTitle,
  filterKey,
  disabled = false,
  useTraduction,
}: DurationDropdownProps<UseTranslation>) {
  const t = useTranslations('Destinations.Hero.Dropdown.Options');
  const [filter, setFilter] = useQueryState(
    'filters',
    filterParamsParser.withOptions({ shallow: false }),
  );
  const [open, setOpen] = React.useState(false);

  const isTranslatedLabel = (label: any): label is TFuncArg =>
    useTraduction === true;

  const renderLabel = (el: Option<UseTranslation>) => {
    if (isTranslatedLabel(el.label)) {
      return t(el.label);
    }
    return el.label;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={className}
          disabled={disabled}
        >
          <div className="flex flex-col items-start min-w-0 overflow-ellipsis">
            <span className="font-bold text-black">{title}</span>
            <span className="ml-auto">
              {filter &&
              filter.filter((el) => el.includes(filterKey)).length > 0
                ? options
                    .filter((framework) => filter.includes(framework.value))
                    .map(renderLabel)
                    .join(', ')
                : subTitle}
            </span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandGroup>
              {options.map((framework) => (
                <CommandItem
                  key={framework.value}
                  value={framework.value}
                  onSelect={(currentValue) => {
                    setOpen(false);
                    setFilter((prev) => {
                      if (!prev) {
                        return [currentValue];
                      }
                      const index = prev.indexOf(currentValue);
                      if (index > -1) {
                        return prev.filter((_, i) => i !== index);
                      }
                      prev.push(currentValue);
                      return prev;
                    });
                  }}
                >
                  {renderLabel(framework)}
                  <Check
                    className={cn(
                      'ml-auto',
                      filter?.includes(framework.value)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
